import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { dashApi } from "@/lib/dashApi";
import { fmtValue } from "@/lib/format";
import { EmptyState, ErrorState, LoadingState, PageHeader, Panel, RiskBandPill, SourceBadge, StatusPill } from "@/components/dash/ui";

const TARGET_RULES = (rule: string) =>
  rule.endsWith("_target_at_risk") || rule === "support_sla_at_risk" || rule === "subscription_churn_risk" ||
  rule === "branch_underperformance" || rule === "company_underperformance";

export default function OutlookPage() {
  const q = useQuery({ queryKey: ["outlook"], queryFn: dashApi.outlook });
  const failures = useQuery({ queryKey: ["failures"], queryFn: dashApi.failures });
  const pace = q.data?.pacing;
  const forecasts = q.data?.forecasts;
  const targetAlerts = (failures.data?.alerts ?? []).filter((a) => TARGET_RULES(a.rule));

  return (
    <div className="space-y-6">
      <PageHeader
        title="Month-end Outlook"
        description="Where this month is heading — projected end-of-month results vs target, the forecast for the headline KPIs, and any target-risk alerts that need attention before month-end."
        right={pace && <span className="text-xs text-slate-500">{pace.month.days_remaining} day(s) left · {pace.month.timezone}</span>}
      />

      {q.isLoading ? <LoadingState /> : q.isError ? <ErrorState error={q.error} /> : pace && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {pace.flow.map((r) => (
            <div key={r.key} className="panel panel-pad space-y-1.5">
              <div className="flex items-start justify-between">
                <p className="text-xs font-medium uppercase tracking-wide text-slate-400">{r.label}</p>
                <RiskBandPill band={r.risk_band} />
              </div>
              <p className="text-2xl font-semibold tabular-nums text-white">{fmtValue(r.projected_eom, r.unit)}</p>
              <p className="text-[11px] text-slate-500">
                projected EOM · MTD {fmtValue(r.mtd_actual, r.unit)}
                {r.has_target ? ` · target ${fmtValue(r.target, r.unit)}` : " · no target"}
              </p>
              {r.has_target && r.required_daily_run_rate !== null && (
                <p className="text-[11px] text-slate-500">need {fmtValue(r.required_daily_run_rate, r.unit)}/day · {r.pct_achieved}% achieved</p>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-2">
        <Panel title="Headline forecasts" right={<Link to="/forecasting-v2" className="text-xs font-medium text-brand-300 hover:text-brand-200">All forecasts →</Link>}>
          {!forecasts?.forecasts.length ? <EmptyState compact>No forecasts available.</EmptyState> : (
            <ul className="divide-y divide-ink-500/40">
              {forecasts.forecasts.map((f) => (
                <li key={f.kpi} className="flex items-center justify-between py-2">
                  <span className="text-sm text-slate-200">{f.label}</span>
                  <span className="text-right">
                    <span className="tabular-nums text-white">{f.projected === null ? "—" : fmtValue(f.projected, f.unit)}</span>
                    <span className="ml-2 text-[11px] text-slate-500">{f.direction ?? "n/a"}</span>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Panel>

        <Panel title={`Target-risk alerts (${targetAlerts.length})`} right={<Link to="/failures" className="text-xs font-medium text-brand-300 hover:text-brand-200">All failures →</Link>}>
          {failures.isLoading ? <LoadingState /> : !targetAlerts.length ? (
            <EmptyState compact>No target-risk alerts — pacing and SLAs are within range.</EmptyState>
          ) : (
            <ul className="space-y-2">
              {targetAlerts.map((a) => (
                <li key={a.id} className="flex items-start gap-2.5">
                  <StatusPill status={a.severity} />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-slate-200">{a.title}</p>
                    {a.detail && <p className="text-xs text-slate-500">{a.detail}</p>}
                  </div>
                  {a.source && <SourceBadge code={a.source} />}
                </li>
              ))}
            </ul>
          )}
        </Panel>
      </div>
    </div>
  );
}
